import * as Api from '@ViewModels';
import { useInfiniteQuery, useSuspenseInfiniteQuery } from '@tanstack/react-query';
import { useUserSession } from '../../models/hooks/appStateHooks';
import { reactQueryClient } from '../../web/ReactQueryProvider';

const BASE_QUERY_KEY = 'contact/autocomplete';

export const invalidateContactAutocomplete = () => reactQueryClient.invalidateQueries({ queryKey: [BASE_QUERY_KEY] });

interface EndpointParams {
	fragment: string;
	pageSize: number;
	enforcePageSize?: boolean;
	searchFields?: string;
	groupByDuplicate?: boolean;
}

export function useContactAutocomplete({
	enabled,
	fragment,
	pageSize,
	enforcePageSize = true,
	searchFields,
	groupByDuplicate = false,
	impersonationContext,
}: {
	enabled?: boolean;
	impersonationContext?: Api.IImpersonationContext;
} & EndpointParams) {
	const userSession = useUserSession();
	return useInfiniteQuery({
		enabled,
		queryFn: ({ pageParam }) => {
			return userSession.webServiceHelper.callAsync<Api.IPagedCollection<Api.IContact>>(
				Api.ImpersonationBroker.composeApiUrl({
					queryParams: {
						enforcePageSize,
						fragment,
						groupByDuplicate,
						pageSize,
						searchFields,
						pageToken: pageParam,
					},
					urlPath: 'contact/autocomplete',
					impersonationContext,
				}),
				'GET'
			);
		},
		getNextPageParam: lastPage => lastPage.pageToken,
		queryKey: [
			BASE_QUERY_KEY,
			fragment,
			pageSize,
			enforcePageSize,
			groupByDuplicate,
			searchFields,
			impersonationContext,
		],
		initialPageParam: '',
	});
}

export function useContactAutocompleteSuspenseQuery({
	fragment,
	pageSize,
	enforcePageSize = true,
	searchFields,
	groupByDuplicate = false,
}: EndpointParams) {
	const userSession = useUserSession();
	return useSuspenseInfiniteQuery({
		queryKey: [BASE_QUERY_KEY, fragment, pageSize, enforcePageSize, groupByDuplicate, searchFields],
		queryFn: () => {
			return userSession.webServiceHelper.callAsync<Api.IPagedCollection<Api.IContact>>(
				Api.ImpersonationBroker.composeApiUrl({
					queryParams: {
						enforcePageSize,
						fragment,
						groupByDuplicate,
						pageSize,
						searchFields,
					},
					urlPath: 'contact/autocomplete',
				}),
				'GET'
			);
		},
		getNextPageParam: lastPage => lastPage.pageToken,
		initialPageParam: '',
	});
}
